import { TFunction, useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { FC, useCallback, useMemo } from 'react';
import { DropdownOption } from 'src/common/components/DropdownDS22';
import Icon from 'src/common/components/IconDS22';
import Img from 'src/common/components/Img';
import { PATHS } from 'src/common/constants';
import useOpenCloseToggle from 'src/common/hooks/useOpenCloseToggle';
import { useListingsPageContext } from 'src/admin/listing-editor/providers/ListingsPageProvider';
import bulkUnsnoozeListings from 'src/admin/listing-editor/utils/api/bulkUnsnoozeListings';
import bulkSnoozeNotifications from 'src/admin/listing-editor/utils/bulkSnoozeNotifications';
import { getListingPagePreviewUrl } from 'src/listing/utils/getBookingUrls';
import getSnoozeStatus from 'src/common/utils/getSnoozeStatus';
import Link from 'next/link';
import ActionsDropdown from 'src/common/components/ActionsDropdown';
import DeleteModal from '../DeleteModal';
import PublishAndUnsnoozeModal from '../PublishAndUnsnoozeModal';
import ProgressStatus from './ProgressStatus';
import Header from './Header';
import styles from './ListingCard.module.scss';
import { shouldSubmitListingForReview } from '../../helpers';
import ApprovalModal from '../ApprovalModal';

const WIDTH = 307;
const HEIGHT = 185;

const getActionsOptions = (
  t: TFunction,
  active: boolean,
  underReview: boolean,
  snoozeActive: boolean,
  progress: number,
  hasPayoutMethod: boolean,
  isImpersonatedSession: boolean = false,
): DropdownOption[] => {
  const canPublish = (hasPayoutMethod || isImpersonatedSession)
    && !active && !underReview && progress === 100;
  const canUnpublish = active && !underReview;
  const canUnsnooze = hasPayoutMethod && progress === 100 && snoozeActive;

  return [
    {
      id: 'edit',
      dropdownLabel: t('landing-page--edit'),
      selectedLabel: t('landing-page--edit'),
      disabled: !hasPayoutMethod,
    },
    {
      id: 'preview-listing',
      dropdownLabel: t('landing-page--preview-listing'),
      selectedLabel: t('landing-page--preview-listing'),
    },
    {
      id: 'live-listing',
      dropdownLabel: t('landing-page--live-listing'),
      selectedLabel: t('landing-page--live-listing'),
      disabled: !canUnpublish,
    },
    {
      id: 'snooze',
      dropdownLabel: t('landing-page--snooze'),
      selectedLabel: t('landing-page--snooze'),
      disabled: !canUnpublish,
    },
    {
      id: 'publish',
      dropdownLabel: t('landing-page--publish'),
      selectedLabel: t('landing-page--publish'),
      disabled: !(canPublish || canUnsnooze),
    },
    {
      id: 'unpublish',
      dropdownLabel: t('landing-page--unpublish'),
      selectedLabel: t('landing-page--unpublish'),
      disabled: !canUnpublish,
    },
    {
      id: 'delete',
      dropdownLabel: t('landing-page--delete'),
      selectedLabel: t('landing-page--delete'),
    },
  ];
};

export type ListingCardProps = {
  active: boolean;
  underReview?: boolean;
  photo: string;
  headline: string;
  shortname: string;
  progress: number;
  id: string;
  hasPayoutMethod: boolean;
  snoozeStartDate: string | null;
  snoozeEndDate: string | null;
  /** e.g. "2020-12-10T14:30:07Z" */
  dateModified: string;
  /** e.g. "2020-12-10T14:30:07Z" */
  datePublished: string;
  snoozeListing: (id: string[]) => void;
  isImpersonatedSession?: boolean;
};
const ListingCard: FC<ListingCardProps> = ({
  id,
  active,
  underReview = false,
  photo,
  headline,
  shortname,
  progress,
  hasPayoutMethod,
  snoozeStartDate,
  snoozeEndDate,
  snoozeListing,
  dateModified,
  datePublished,
  isImpersonatedSession,
}) => {
  const [t] = useTranslation('admin-listings');
  const router = useRouter();
  const { close, isOpen, open } = useOpenCloseToggle();
  const {
    close: closeApprovalModal,
    isOpen: isApprovalModalOpen,
    open: openApprovalModal,
  } = useOpenCloseToggle();
  const {
    close: closeUnsnoozeModal,
    isOpen: isUnsnoozeModalOpen,
    open: openUnsnoozeModal,
  } = useOpenCloseToggle();
  const { snoozeActive } = getSnoozeStatus(snoozeStartDate, snoozeEndDate);
  const {
    selectListing,
    selectedListings,
    deselectListing,
    publishListing,
    unpublishListing,
    deleteListing,
    refresh: refreshPage,
  } = useListingsPageContext();

  const listingUrlOwnerAdmin = useMemo(() => ({
    pathname: `${PATHS.ADMIN_LISTING_EDITOR}${id}/`,
    query: router.query,
  }), [id, router.query]);
  const listingUrlLive = `${PATHS.LISTING}${id}/`;

  const actionOptions = getActionsOptions(
    t,
    Boolean(active),
    Boolean(underReview),
    snoozeActive,
    progress,
    hasPayoutMethod,
    isImpersonatedSession,
  );
  const handleOnChange = useCallback((checked) => {
    if (checked) {
      selectListing(id);
    } else {
      deselectListing(id);
    }
  }, [deselectListing, id, selectListing]);

  const handleListingReview = useCallback((listingId: string) => {
    const canBeReviewed = shouldSubmitListingForReview(active, dateModified, datePublished);
    if (!canBeReviewed) {
      publishListing(listingId);
      return;
    }
    openApprovalModal();
  }, [active, dateModified, datePublished, openApprovalModal, publishListing]);

  const handleOnSelect = useCallback((option: DropdownOption) => {
    switch (option.id) {
      case 'publish': {
        if (active && snoozeActive) {
          openUnsnoozeModal();
        } else {
          handleListingReview(id);
        }
        break;
      }
      case 'unpublish': {
        unpublishListing(id);
        break;
      }
      case 'preview-listing': {
        window.open(getListingPagePreviewUrl(id));
        break;
      }
      case 'live-listing': {
        window.open(listingUrlLive);
        break;
      }
      case 'delete': {
        open();
        break;
      }
      case 'snooze': {
        snoozeListing([id]);
        break;
      }
      default: {
        router.push(listingUrlOwnerAdmin);
        break;
      }
    }
  }, [
    handleListingReview,
    active,
    id,
    unpublishListing,
    listingUrlLive,
    open,
    snoozeListing,
    router,
    listingUrlOwnerAdmin,
    openUnsnoozeModal,
    snoozeActive,
  ]);

  const handleDelete = useCallback(async () => {
    await deleteListing(id);
  }, [deleteListing, id]);

  const handleUnsnooze = useCallback(async () => {
    const result = await bulkUnsnoozeListings([id]);
    closeUnsnoozeModal();
    bulkSnoozeNotifications(!result.success, 'publish-and-unsnooze');
    refreshPage();
  }, [id, closeUnsnoozeModal, refreshPage]);

  const checked = selectedListings.includes(id);
  const listingCoverImage = (
    <Img
      withPlaceholder
      className={styles.img}
      alt={headline}
      width={WIDTH}
      height={HEIGHT}
      src={photo}
      imageParams={{
        fit: 'crop',
        w: WIDTH,
        h: HEIGHT,
      }}
    />
  );

  return (
    <div className={styles.root}>
      <Header
        active={active}
        underReview={underReview}
        checked={checked}
        onChange={handleOnChange}
        snoozeStartDate={snoozeStartDate}
        snoozeEndDate={snoozeEndDate}
      />
      {hasPayoutMethod ? (
        (
          <Link href={listingUrlOwnerAdmin}>
            {listingCoverImage}
          </Link>
        )
      ) : listingCoverImage}
      <p className={styles.description}>
        {hasPayoutMethod ? (
          <Link href={listingUrlOwnerAdmin}>
            {headline}
          </Link>
        ) : headline}
      </p>
      <div className={styles.footer}>
        <ProgressStatus progress={progress} />
        <ActionsDropdown
          classNameModifier="listingCard"
          options={actionOptions}
          variant="whiteCompact"
          showCaret={false}
          handleSelect={handleOnSelect}
          label={<Icon id="dots-three" />}
          dropdownPosition="right"
        />
      </div>
      <DeleteModal
        handleDelete={handleDelete}
        open={isOpen}
        closeModal={close}
        selectedListingsCount={1}
      />
      <PublishAndUnsnoozeModal
        handleUnsnooze={handleUnsnooze}
        open={isUnsnoozeModalOpen}
        closeModal={closeUnsnoozeModal}
      />
      <ApprovalModal
        listings={[{
          id,
          shortname,
        }]}
        isOpen={isApprovalModalOpen}
        close={closeApprovalModal}
        afterSubmit={refreshPage}
      />
    </div>
  );
};

export default ListingCard;
